import {
    clampSproutStreamSelection,
    DEFAULT_SPROUT_STREAM_MODEL_ID,
    DEFAULT_SPROUT_STREAM_REASONING_EFFORT,
    isRecord,
    parseSproutStreamOverrides,
    SPROUT_AGENT,
    type SproutStreamModelId,
    type SproutStreamReasoningEffort,
} from '@fruitful/llm-service-shared';

import { StorageKeys } from '../../common/constants';
import type { IStorage } from '../../common/types/Storage';

const MAX_STORED_THREADS = 20;

export interface SproutStreamSelection {
    modelId: SproutStreamModelId;
    reasoningEffort: SproutStreamReasoningEffort;
}

interface StoredThreadSelection {
    threadId: string;
    selection: SproutStreamSelection;
}

interface StoredSproutStreamOptions {
    lastUsed: SproutStreamSelection;
    byThread: StoredThreadSelection[];
    agentKey: string;
}

export const DEFAULT_SPROUT_STREAM_SELECTION: SproutStreamSelection = {
    modelId: DEFAULT_SPROUT_STREAM_MODEL_ID,
    reasoningEffort: DEFAULT_SPROUT_STREAM_REASONING_EFFORT,
};

const toSelection = (value: unknown): SproutStreamSelection | null => {
    if (!isRecord(value)) {
        return null;
    }

    const parsed = parseSproutStreamOverrides({
        model: value.modelId ?? value.model,
        reasoningEffort: value.reasoningEffort,
    });
    if (parsed.model == null || parsed.reasoningEffort == null) {
        return null;
    }

    return clampSproutStreamSelection({
        modelId: parsed.model,
        reasoningEffort: parsed.reasoningEffort,
    });
};

const toStoredThreadSelection = (
    value: unknown,
): StoredThreadSelection | null => {
    if (!isRecord(value) || typeof value.threadId !== 'string') {
        return null;
    }

    const selection = toSelection(value.selection);
    if (selection == null) {
        return null;
    }

    return { threadId: value.threadId, selection };
};

const parseStore = (
    raw: string | null | undefined,
): StoredSproutStreamOptions => {
    const fallback: StoredSproutStreamOptions = {
        lastUsed: DEFAULT_SPROUT_STREAM_SELECTION,
        byThread: [],
        agentKey: SPROUT_AGENT.agentKey,
    };
    if (raw == null || raw === '') {
        return fallback;
    }

    try {
        const parsed: unknown = JSON.parse(raw);
        if (!isRecord(parsed)) {
            return fallback;
        }

        return {
            lastUsed:
                toSelection(parsed.lastUsed) ?? DEFAULT_SPROUT_STREAM_SELECTION,
            byThread: Array.isArray(parsed.byThread)
                ? parsed.byThread.flatMap((entry) => {
                      const stored = toStoredThreadSelection(entry);
                      return stored == null ? [] : [stored];
                  })
                : [],
            agentKey:
                typeof parsed.agentKey === 'string' && parsed.agentKey !== ''
                    ? parsed.agentKey
                    : SPROUT_AGENT.agentKey,
        };
    } catch {
        return fallback;
    }
};

export const readSproutStreamOptionsStore = (
    storage: IStorage,
): StoredSproutStreamOptions =>
    parseStore(storage.getString(StorageKeys.SPROUT_STREAM_OPTIONS));

export const readSproutStreamSelectionForThread = (
    storage: IStorage,
    threadId: string,
): SproutStreamSelection | null =>
    readSproutStreamOptionsStore(storage).byThread.find(
        (entry) => entry.threadId === threadId,
    )?.selection ?? null;

export const writeSproutStreamSelection = (
    storage: IStorage,
    selection: SproutStreamSelection,
    threadId: string | undefined,
): void => {
    const clamped = clampSproutStreamSelection(selection);
    const stored = readSproutStreamOptionsStore(storage);
    const byThread =
        threadId == null || threadId === ''
            ? stored.byThread
            : [
                  ...stored.byThread.filter(
                      (entry) => entry.threadId !== threadId,
                  ),
                  { threadId, selection: clamped },
              ].slice(-MAX_STORED_THREADS);

    storage.setString(
        StorageKeys.SPROUT_STREAM_OPTIONS,
        JSON.stringify({
            lastUsed: clamped,
            byThread,
            agentKey: stored.agentKey,
        }),
    );
};

export const readStoredMemberAgentKey = (storage: IStorage): string =>
    readSproutStreamOptionsStore(storage).agentKey;

export const writeStoredMemberAgentKey = (
    storage: IStorage,
    agentKey: string,
): void => {
    const stored = readSproutStreamOptionsStore(storage);
    storage.setString(
        StorageKeys.SPROUT_STREAM_OPTIONS,
        JSON.stringify({
            lastUsed: stored.lastUsed,
            byThread: stored.byThread,
            agentKey,
        }),
    );
};
